import {Injector} from '@angular/core';
import * as _ from 'lodash-es';
import {Dimension, END, Point, START} from './abstract-units';
import {Cell, CellDisplayInfo} from './cell';
import {BOX_EDGES_MAP, BoxEdge, BoxEdges, Edge, EdgeDisplayInfo, Side} from './edge';
import {Guideline} from './guideline';
import {CellActions} from './layout-actions/cell-actions';
import {EdgeActions} from './layout-actions/edge-actions';
import {WidgetActions} from './layout-actions/widget-actions';
import {LayoutBase} from './layout-base';
import {LayoutHistory} from './layout-history/layout-history';
import {Widget} from './widget/widget';
import {Wireframe} from './wireframe';

export class Layout extends LayoutBase {

    public static bindEdgesToCells(edges: Edge[]): void {
        edges.forEach(edge => {
            const axis = edge.axis;

            edge.sides.forEach((sideCells, side: Side) => {
                sideCells.forEach(cell => {
                    const boxEdge: BoxEdge = BOX_EDGES_MAP[axis][side];

                    cell.edges[boxEdge] = edge;
                });
            });
        });
    }

    public static compareCellsOrderOnAxis(axis: Dimension, cell1: Cell, cell2: Cell): number {
        const boxEdge: BoxEdge = axis === Dimension.X ? BoxEdge.LEFT : BoxEdge.TOP;
        const value1 = cell1.edges[boxEdge].movingAxisPos;
        const value2 = cell2.edges[boxEdge].movingAxisPos;

        return value1 - value2; // ASC order.
    }

    public edges: Edge[];

    public wireframes: Wireframe[];
    public guidelines: Guideline[];

    public edgeActions: EdgeActions = new EdgeActions(this);
    public cellActions: CellActions = new CellActions(this);
    public widgetActions: WidgetActions = new WidgetActions(this);

    public history: LayoutHistory = new LayoutHistory();

    constructor(
        size: Point,
        tabId: string,
        cells: Cell[],
        edges: Edge[],
        widgets: Widget[],
        injector: Injector
    ) {
        super(size, tabId, cells, widgets, injector);

        this.edges = edges.slice();

        console.log('[Layout] Initialization', {edges: this.edges, cells: this.cells});
    }

    public override produceDisplayInfo(displaySize: Point): void {
        this.displaySize = displaySize.slice() as Point;

        this.updateEdgesDisplayInfo();
        this.updateCellsDisplayInfo();
    }

    public override setLayoutSize(newLayoutSize: Point): void {
        super.setLayoutSize(newLayoutSize);

        this.edges.forEach(edge => {
            if (edge.display) {
                if (edge.display.movingAxisPos !== undefined && edge.display.movingAxisPos !== null) {
                    edge.movingAxisPos = edge.display.movingAxisPos;
                }
                if (edge.display.axisRange) {
                    edge.axisRange = edge.display.axisRange.slice() as Point;
                }
            }
        });
    }

    public setWireframes(wireframes: Wireframe[]): void {
        this.wireframes = wireframes;

        this.cells.forEach(item => item.display.wireframe = null);

        if (wireframes) {
            wireframes.forEach(wireframe => wireframe.cell.display.wireframe = wireframe);
        }
    }

    public setGuidelines(guidelines: Guideline[]): void {
        this.guidelines = guidelines;
    }

    public override updateCellsDisplayInfo(cells?: Cell[]): void {
        cells = cells || this.cells;

        cells.forEach(cell => {
            const boxEdges: BoxEdges = cell.edges;
            const topLeftPos: Point = [
                boxEdges[BoxEdge.LEFT].display.movingAxisPos,
                boxEdges[BoxEdge.TOP].display.movingAxisPos
            ];
            const rightBottomNextPos: Point = [
                boxEdges[BoxEdge.RIGHT].display.movingAxisPos,
                boxEdges[BoxEdge.BOTTOM].display.movingAxisPos
            ];
            const displayInfo: CellDisplayInfo = cell.display = cell.display || {
                pos:       null,
                nextPos:   null,
                size:      null,
                wireframe: null
            };

            displayInfo.pos = topLeftPos;
            displayInfo.nextPos = rightBottomNextPos;
            displayInfo.size = [
                rightBottomNextPos[Dimension.X] - topLeftPos[Dimension.X],
                rightBottomNextPos[Dimension.Y] - topLeftPos[Dimension.Y]
            ];
        });
    }

    public updateEdgesDisplayInfo(edges?: Edge[]): void {
        edges = edges || this.edges;

        edges.forEach(edge => {
            const displayInfo: EdgeDisplayInfo = edge.display = edge.display || {
                movingAxisPos: null,
                axisRange: null
            };

            displayInfo.movingAxisPos = this.calcDisplayDimension(edge.movingAxisPos, edge.movingAxis);
            displayInfo.axisRange = [
                this.calcDisplayDimension(edge.axisRange[START], edge.axis),
                this.calcDisplayDimension(edge.axisRange[END], edge.axis)
            ];
        });
    }

    public sortEdgeCells(edge: Edge): void {
        const cellsComparator = Layout.compareCellsOrderOnAxis.bind(null, edge.axis);

        edge.sides.forEach(sideCells => sideCells.sort(cellsComparator));
    }

    public sortEdgesCells(edges?: Edge[]): void {
        edges = edges || this.edges;

        edges.forEach(edge => this.sortEdgeCells(edge));
    }

    public sortEdgeSideCells(edge: Edge, side: Side): void {
        const cellsComparator = Layout.compareCellsOrderOnAxis.bind(this, edge.axis);

        edge.sides[side].sort(cellsComparator);
    }

    public getEdgeCells(edge: Edge): Cell[] {
        return edge.sides[Side.FIRST].concat(edge.sides[Side.SECOND]);
    }

    public getEdgesCells(edges: Edge[]): Cell[] {
        return _.union(
            ...edges.map(edge => this.getEdgeCells(edge))
        );
    }

}
